import PropTypes from "prop-types";

function SpinnerVisual({ size = "md", tone = "primary" }) {
  const sizeClasses = {
    sm: "h-6 w-6 border-2",
    md: "h-12 w-12 border-4",
    lg: "h-16 w-16 border-4",
  };

  const toneClasses = {
    primary: "border-primary/30 dark:border-primary/20 border-t-primary dark:border-t-primary",
    slate: "border-slate-200 dark:border-slate-800 border-t-slate-600 dark:border-t-slate-300",
    white: "border-white/30 border-t-white",
  };

  const classNames = [
    "inline-flex rounded-full animate-spin border-solid",
    sizeClasses[size],
    toneClasses[tone],
  ];

  return <span className={classNames.join(" ")} aria-hidden="true" />;
}

SpinnerVisual.propTypes = {
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  tone: PropTypes.oneOf(["primary", "slate", "white"]),
};

function SpinnerCopy({ message }) {
  if (!message) return null;
  return (
    <p className="text-base font-semibold text-primary dark:text-primary/80">
      {message}
    </p>
  );
}

SpinnerCopy.propTypes = {
  message: PropTypes.string,
};

export function FullscreenSpinner({ message = "Loading...", tone, size }) {
  return (
    <div className="flex w-full min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
      <SpinnerVisual size={size} tone={tone} />
      <SpinnerCopy message={message} />
    </div>
  );
}

FullscreenSpinner.propTypes = {
  message: PropTypes.string,
  tone: PropTypes.oneOf(["primary", "slate", "white"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
};

export function OverlaySpinner({
  message = "Working...",
  tone,
  size = "md",
  blurred = true,
}) {
  const overlayClasses = [
    "absolute inset-0 z-40 flex items-center justify-center",
  ];
  if (blurred) {
    overlayClasses.push("bg-white/70 dark:bg-gray-950/70 backdrop-blur-sm");
  }

  return (
    <div className={overlayClasses.join(" ")} role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <SpinnerVisual size={size} tone={tone} />
        <SpinnerCopy message={message} />
      </div>
    </div>
  );
}

OverlaySpinner.propTypes = {
  message: PropTypes.string,
  tone: PropTypes.oneOf(["primary", "slate", "white"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  blurred: PropTypes.bool,
};

export function InlineSpinner({
  message,
  tone,
  size = "sm",
  className,
  messageClassName,
}) {
  const wrapperClasses = ["inline-flex items-center gap-2"];
  if (className) {
    wrapperClasses.push(className);
  }

  const messageClasses = messageClassName
    ? ["text-sm font-medium", messageClassName].join(" ")
    : "text-sm font-medium text-primary dark:text-primary/80";

  return (
    <span className={wrapperClasses.join(" ")}>
      <SpinnerVisual size={size} tone={tone} />
      {message ? (
        <span className={messageClasses}>
          {message}
        </span>
      ) : null}
    </span>
  );
}

InlineSpinner.propTypes = {
  message: PropTypes.string,
  tone: PropTypes.oneOf(["primary", "slate", "white"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  messageClassName: PropTypes.string,
};
